import { normalize, schema, denormalize } from 'normalizr'
import { has, prop, propEq, compose, findIndex, curry } from 'ramda'

/** feature */
const addParentReference = (value, parent) => ({
  ...value,
  fnId: parent.uri,
})

const addGrandparentReference = (value, parent) => ({
  ...value,
  fnId: parent.fnId,
})

const getParentHysteresis = (value, parent) => {
  const conditionUri = prop('uri', value)
  if (!conditionUri) return {}

  const slots = [
    ['queryA', 'queryAHysteresis'],
    ['queryB', 'queryBHysteresis'],
    ['queryC', 'queryCHysteresis'],
    ['queryD', 'queryDHysteresis'],
  ]

  for (const [slotKey, hystKey] of slots) {
    if (parent[slotKey] === conditionUri && parent[hystKey] !== undefined) {
      return { hysteresis: parent[hystKey] }
    }
  }

  return {}
}

const conditionsProcessStrategy = (value, parent) => {
  const hasParentQueryType = has('queryType', parent)
  return {
    ...value,
    ...(hasParentQueryType && { parentType: prop('queryType', parent) }),
    ...makeConditionRungIndex(value, parent),
    ...getParentHysteresis(value, parent),
  }
}

const makeConditionRungIndex = (value, parent) => {
  const parentQueryUri = prop('uri', parent)
  const isCurrentConditionParentQuery = propEq(
    'parentQueryUri',
    parentQueryUri,
    value
  )

  if (isCurrentConditionParentQuery) {
    const uri = prop('uri', value)
    const currentConditionListIndex = compose(
      findIndex(propEq('uri', uri)),
      prop('conditions')
    )

    return {
      conditionIndex: currentConditionListIndex(parent),
    }
  }

  return {}
}

const condition = new schema.Entity(
  'conditions',
  {},
  {
    idAttribute: 'uri',
    processStrategy: conditionsProcessStrategy,
  }
)

const scalingCondition = new schema.Entity(
  'conditions',
  {},
  {
    idAttribute: 'uri',
  }
)

const query = new schema.Entity(
  'queries',
  {
    conditions: [condition],
  },
  {
    idAttribute: 'uri',
  }
)

const groupQueryType = new schema.Array(
  {
    queries: query,
    conditions: condition,
  },
  ({ queryType }) => {
    if (queryType === 'complex' || queryType === 'timer') return 'queries'
    return 'conditions'
  }
)

const output = new schema.Entity(
  'outputs',
  {
    scalingQuery: scalingCondition,
  },
  {
    idAttribute: 'id',
    processStrategy: addGrandparentReference,
  }
)

const outputMap = new schema.Entity(
  'outputMaps',
  {
    outputs: [output],
  },
  {
    idAttribute: (_, parent) => parent.uri,
    processStrategy: addParentReference,
  }
)

const featureFn = new schema.Entity(
  'functions',
  {
    outputMap,
  },
  { idAttribute: 'uri' }
)

const featureSchema = new schema.Entity('feature', {
  queries: groupQueryType,
  functions: [featureFn],
})

export const normalizeFeature = (data) => {
  const { entities } = normalize(data, featureSchema)
  return entities
}

export const denormalizeFeature = curry((id, data) => {
  const featureId = id || Object.keys(data.feature)[0]
  return denormalize(featureId, featureSchema, data)
})

/** snapshots */

const snapshotData = (value) => {
  const { id, uri, user, date, object } = value
  return {
    id,
    uri,
    user,
    date,
    feature: object.feature,
  }
}

const featureSnapshots = new schema.Entity(
  'snapshots',
  {},
  {
    idAttribute: 'uri',
    processStrategy: snapshotData,
  }
)

export const featureSnapshotsSchema = [featureSnapshots]

const signalsData = (value) => {
  const { inputs } = value
  return inputs.reduce(
    (acc, input) => ({
      ...acc,
      [input.uri]: input,
    }),
    {}
  )
}
const signalModule = new schema.Entity(
  'signals',
  {},
  {
    idAttribute: 'compatibleModule',
    processStrategy: signalsData,
  }
)

export const normalizeSignals = (inputsByModules) =>
  normalize(inputsByModules, [signalModule])

const pinData = (value) => {
  const { outputs } = value
  return outputs.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.id]: curr,
    }),
    {}
  )
}
const pinModule = new schema.Entity(
  'outputPins',
  {},
  {
    idAttribute: 'compatibleModule',
    processStrategy: pinData,
  }
)

export const normalizeOutputPins = (inputsByModules) =>
  normalize(inputsByModules, [pinModule])
